// CSV Export Functions

import type { Lead } from './types';
import { formatDateOnly, formatValue } from './utils';

/**
 * Konvertiert Leads zu CSV
 * Dynamische Spalten basierend auf allen vorhandenen fields
 */
export function leadsToCSV(leads: Lead[]): string {
  if (leads.length === 0) {
    return 'Keine Daten zum Exportieren';
  }

  // Alle einzigartigen field-Keys sammeln
  const allFieldKeys = new Set<string>();
  leads.forEach((lead) => {
    Object.keys(lead.fields).forEach((key) => allFieldKeys.add(key));
  });

  const fieldKeys = Array.from(allFieldKeys).sort();

  // CSV-Header
  const headers = [
    'Lead-ID',
    'Status',
    'Datum',
    'Kunde',
    'Formular-Typ',
    'Kontakt Name',
    'Kontakt E-Mail',
    'Kontakt Telefon',
    ...fieldKeys.map((key) => {
      // Versuche, Label zu finden (aus erstem Lead, der dieses Feld hat)
      const lead = leads.find((l) => l.fields[key] !== undefined);
      return lead?.field_labels[key] || key;
    }),
    'Weitergeleitet am',
    'Notizen',
  ];

  // CSV-Zeilen
  const rows = leads.map((lead) => {
    const baseFields = [
      lead.id,
      lead.status,
      formatDateOnly(lead.created_at),
      lead.customer?.name || lead.customer_slug,
      lead.form_identifier,
      escapeCsvValue(lead.contact_name),
      lead.contact_email,
      lead.contact_phone || '',
    ];

    // Dynamische Fields
    const dynamicFields = fieldKeys.map((key) => {
      const value = lead.fields[key];
      return escapeCsvValue(formatValue(value));
    });

    const metaFields = [
      lead.forwarded_at ? formatDateOnly(lead.forwarded_at) : '',
      escapeCsvValue(lead.notes || ''),
    ];

    return [...baseFields, ...dynamicFields, ...metaFields];
  });

  // CSV zusammenbauen
  const csvContent = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n');

  return csvContent;
}

/**
 * CSV-Wert escapen (Kommas, Quotes, Zeilenumbrüche)
 */
function escapeCsvValue(value: string): string {
  if (value === null || value === undefined) return '';
  
  const stringValue = String(value);
  
  // Wenn Komma, Quote oder Zeilenumbruch enthalten, in Quotes wrappen
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    // Quotes verdoppeln
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
}

/**
 * CSV-Download triggern
 */
export function downloadLeadsCSV(leads: Lead[], filename?: string) {
  const csv = leadsToCSV(leads);
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM für Excel
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  const dateString = new Date().toISOString().split('T')[0];
  const defaultFilename = `leads_export_${dateString}.csv`;
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename || defaultFilename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
